// 🚨 We should try to get the complexity the mapping function inside the <ContentWrapper> down by splitting
// it up into smaller components. This will make it easier to read and maintain.
/* eslint-disable complexity */
import {
  ContentBlocks_ThreeColumnsModule_Column,
  ContentBlocks_ThreeColumnsModule as ThreeColumnsModuleType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {
  ContentBlockCTA,
  ContentBlockImageTile,
  ContentBlockText,
  RowSection,
  desktopAndUp,
  tabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useId} from 'react';
import sc, {css} from 'styled-components';
import {useContentBlockPromotionTracking} from '../helpers/useContentBlockPromotionTracking';
import {useContentBlockPromotionTrackingImpression} from '../helpers/useContentBlockPromotionTrackingImpression';
import {alignmentMap, fontMap, styleMap, transformImage} from '../transforms';

export const ThreeColumnsModule: React.FC<{
  content: ThreeColumnsModuleType;
  blockId?: number;
  testId?: string;
}> = ({content, blockId, testId = 'customPageThreeColumnModule'}) => {
  const columns = [content.first, content.second, content.third];

  const uniqueId = useId();
  const {handleCTAClick, placementRef} = useTrackContentBlock(columns, blockId);

  return (
    <BlockWrapper
      $backgroundColor={content.backgroundColor}
      centerContent
      data-content-block={blockId}
      ref={placementRef}
      data-testid={testId}
    >
      <ContentWrapper>
        {columns.map((column, index) => {
          const row = index + 1;
          return (
            <React.Fragment key={row}>
              <StyledContentBlockImageTile
                testId={`${testId}ImageTile`}
                image={transformImage(column.image)}
                $gridArea={`${AREAS.IMAGE_TILE}${row}`}
                $isAnElementBelow={Boolean(
                  column.headline?.text !== '' ||
                    column.text?.text !== '' ||
                    column.firstCta ||
                    column.secondCta,
                )}
                $isLastElement={Boolean(
                  (!column.headline || column.headline.text === '') &&
                    (!column.text || column.text.text === '') &&
                    !column.firstCta &&
                    !column.secondCta,
                )}
              />

              {column.headline && column.headline.text !== '' && (
                <Spacer
                  $gridArea={`${AREAS.HEADLINE}${row}`}
                  $isLastElement={Boolean(
                    (!column.text || column.text.text === '') &&
                      !column.firstCta &&
                      !column.secondCta,
                  )}
                >
                  <ContentBlockText
                    testId={`${testId}Headline`}
                    alignment={alignmentMap[column.headline.alignment]}
                    color={column.headline.color}
                    size={fontMap[column.headline.size]}
                    text={column.headline.text}
                    weight="bold"
                  />
                </Spacer>
              )}

              {column.text && column.text.text !== '' && (
                <Spacer
                  $gridArea={`${AREAS.TEXT}${row}`}
                  $isLastElement={Boolean(
                    !column.firstCta && !column.secondCta,
                  )}
                >
                  <ContentBlockText
                    alignment={alignmentMap[column.text.alignment]}
                    color={column.text.color}
                    size={fontMap[column.text.size]}
                    text={column.text.text}
                    testId={`${testId}Text`}
                    isAParagraph
                  />
                </Spacer>
              )}

              {(column.firstCta || column.secondCta) && (
                <CTAsWrapper
                  $gridArea={`${AREAS.CTA}${row}`}
                  moreThanOne={Boolean(column.firstCta && column.secondCta)}
                >
                  {[
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- filter(Boolean) below ensures non-null
                    (column.firstCta && {
                      text: {
                        text: column.firstCta.text.text,
                        style:
                          // Only for 3, and 2 cols module the default (0 - undefined) style is text-link
                          column.firstCta.text.style !== 0
                            ? styleMap[column.firstCta.text.style]
                            : styleMap['3'],
                        textColor: column.firstCta.text.color,
                      },
                      link: column.firstCta.link,
                      backgroundColor: column.firstCta.backgroundColor,
                    })!,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- filter(Boolean) below ensures non-null
                    (column.secondCta && {
                      text: {
                        text: column.secondCta.text.text,
                        style:
                          // Only for 3, and 2 cols module the default (0 - undefined) style is text-link
                          column.secondCta.text.style !== 0
                            ? styleMap[column.secondCta.text.style]
                            : styleMap['3'],
                        textColor: column.secondCta.text.color,
                      },
                      link: column.secondCta.link,
                      backgroundColor: column.secondCta.backgroundColor,
                    })!,
                  ]
                    .filter(Boolean)
                    .map(
                      (cta, ctaIndex) =>
                        cta.text.text !== '' && (
                          <StyledContentBlockCTA
                            testId={`${uniqueId}-${testId}CTA-${index}-${ctaIndex}`}
                            key={ctaIndex}
                            $gridArea={`${INNER_CTA}${ctaIndex + 1}`}
                            onClick={() =>
                              handleCTAClick(
                                cta.text.text,
                                cta.link,
                                index,
                                ctaIndex + 1,
                              )
                            }
                            {...cta}
                          />
                        ),
                    )}
                </CTAsWrapper>
              )}
            </React.Fragment>
          );
        })}
      </ContentWrapper>
    </BlockWrapper>
  );
};

const useTrackContentBlock = (
  columns: Array<ContentBlocks_ThreeColumnsModule_Column>,
  blockId?: number,
) => {
  const promotionTrackingClick = useContentBlockPromotionTracking();
  const locationBlockType = 'threeColumnsModule';

  const handleCTAClick = (
    promotionName: string,
    path: {url: string; external: boolean},
    streamPosition: number,
    ctaPosition: number,
  ) => {
    promotionTrackingClick({
      actionType: 'click',
      contentBlockSource: 'cta',
      locationBlockType,
      promotionId: blockId ? String(blockId) : '',
      blockId,
      streamPosition,
      promotionName,
      elementPosition: ctaPosition,
      path,
    });
  };

  const {imageTileCTALink, placementImpressionpromotionName} =
    getPlacementTrackingData(columns);
  const {elementRef: placementRef} =
    useContentBlockPromotionTrackingImpression<HTMLDivElement>({
      blockId,
      promotionName: placementImpressionpromotionName,
      promotionId: blockId ? String(blockId) : '',
      locationBlockType,
      path: imageTileCTALink || {external: false, url: ''},
      contentBlockSource: 'banner',
    });

  return {
    handleCTAClick,
    placementRef,
  };
};

const getPlacementTrackingData = (
  columns: Array<ContentBlocks_ThreeColumnsModule_Column>,
) => {
  const placementImpressionpromotionName = columns
    .reduce((acc: string[], curr) => {
      if (curr.firstCta) {
        return [...acc, curr.firstCta.text.text];
      } else if (curr.secondCta) {
        return [...acc, curr.secondCta.text.text];
      } else if (curr.headline) {
        return [...acc, curr.headline.text];
      }
      return acc;
    }, [])
    .find(name => name !== '');

  const imageTileCTALink = columns
    .reduce((acc: Array<{url: string; external: boolean}>, curr) => {
      if (curr.firstCta) {
        return [...acc, curr.firstCta.link];
      } else if (curr.secondCta) {
        return [...acc, curr.secondCta.link];
      }
      return acc;
    }, [])
    .find(link => link.url !== '');

  return {
    placementImpressionpromotionName,
    imageTileCTALink,
  };
};

const BlockWrapper = linariaStyled(RowSection)<{$backgroundColor?: string}>`
  background-color: ${({$backgroundColor}) => $backgroundColor ?? ''};
`;

enum AREAS {
  IMAGE_TILE = 'imageTile',
  HEADLINE = 'headline',
  TEXT = 'text',
  CTA = 'cta',
}

const INNER_CTA = 'InnerCTA';

const ContentWrapper = sc.div`
  column-gap: 3rem;
  display: grid;
  grid-auto-columns: 1fr;
  max-width: 1440px;
  padding: 1rem;
  width: 100%;

  grid-template-areas:
    '${AREAS.IMAGE_TILE}1'
    '${AREAS.HEADLINE}1'
    '${AREAS.TEXT}1'
    '${AREAS.CTA}1'
    '${AREAS.IMAGE_TILE}2'
    '${AREAS.HEADLINE}2'
    '${AREAS.TEXT}2'
    '${AREAS.CTA}2'
    '${AREAS.IMAGE_TILE}3'
    '${AREAS.HEADLINE}3'
    '${AREAS.TEXT}3'
    '${AREAS.CTA}3';

  ${tabletAndUp(css`
    grid-template-areas:
      '${AREAS.IMAGE_TILE}1 ${AREAS.IMAGE_TILE}2 ${AREAS.IMAGE_TILE}3'
      '${AREAS.HEADLINE}1 ${AREAS.HEADLINE}2 ${AREAS.HEADLINE}3'
      '${AREAS.TEXT}1 ${AREAS.TEXT}2 ${AREAS.TEXT}3'
      '${AREAS.CTA}1 ${AREAS.CTA}2 ${AREAS.CTA}3';

    grid-template-columns: repeat(3, minmax(calc(33.33% - 2rem), 1fr));
    padding: 1rem 2rem 4rem 2rem;
  `)}
`;

const StyledContentBlockImageTile = sc(ContentBlockImageTile)<{
  $gridArea: string;
  $isAnElementBelow: boolean;
  $isLastElement: boolean;
}>`
  border-radius: 5px;
  grid-area: ${({$gridArea}) => $gridArea};
  margin-bottom: ${({$isLastElement}) => ($isLastElement ? '3rem' : '1rem')};
  overflow: hidden;

  ${({$isAnElementBelow}) =>
    !$isAnElementBelow &&
    tabletAndUp(css`
      margin-bottom: 0;
    `)}
`;

const Spacer = sc.div<{
  $gridArea: string;
  $isLastElement: boolean;
}>`
  margin: 1rem 0;
  grid-area: ${({$gridArea}) => $gridArea};

  ${({$isLastElement}) =>
    $isLastElement &&
    css`
      margin-bottom: 3rem;
    `}
`;

const CTAsWrapper = sc.div<{
  $gridArea: string;
  moreThanOne: boolean;
}>`
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-area: ${({$gridArea}) => $gridArea};
  grid-template-areas: ${({moreThanOne}) =>
    moreThanOne ? `'${INNER_CTA}1 ${INNER_CTA}2'` : `'${INNER_CTA}1'`};
  justify-content: ${({moreThanOne}) =>
    moreThanOne ? 'space-around' : 'center'};
  margin-top: 1rem;
  margin-bottom: 3rem;

  ${tabletAndUp(css`
    margin-bottom: 1rem;
  `)}
`;

const StyledContentBlockCTA = sc(ContentBlockCTA)<{$gridArea: string}>`
  grid-area: ${({$gridArea}) => $gridArea};

  ${tabletAndUp(css`
    & span {
      padding: 16px 24px;
    }
  `)}

  ${desktopAndUp(css`
    & span {
      padding: 16px 40px;
    }
  `)}
`;
